import { defineComponent as _defineComponent } from 'vue'
import {
  computed, PropType, ref, toRefs, watch
} from 'vue';
import BCXSortableTableHeader from '@/components/molecules/BCXSortableTableHeader.vue';
import BCXPaginatedContent from '@/components/molecules/BCXPaginatedContent.vue';
import useResponsiveness from '@/mixins/useResponsiveness';
import SearchInput from '@/components/molecules/SearchInput.vue';
import { BcxTableFilter } from '@/models/BcxTable';
import BCXMultiselectFilter from '@/components/molecules/BCXMultiselectFilter.vue';
import {
  reactiveComputed, useLocalStorage, watchImmediate
} from '@vueuse/core';
import BCXFilterButtons from '@/components/BCXFilterButtons.vue';
import useBcxTableSearchFilterSort, { BcxTablePinnedItemPosition, BcxTableSearchFilterSortOptions } from '@/mixins/useBcxTableSearchFilterSort';
import BCXPaginationSwitcher from '@/components/molecules/BCXPaginationSwitcher.vue';
import BCXPagination from '@/components/molecules/BCXPagination.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'BCXTable',
  props: {
  fieldNames: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  headerFields: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  headerTitles: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  mobileHiddenFields: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  rows: {
    type: Array as PropType<unknown[]>,
    default: () => []
  },
  templateColumns: {
    type: String,
    default: '1fr'
  },
  mobileTemplateColumns: {
    type: String,
    default: '1fr'
  },
  mobileGridAreas: {
    type: String,
    default: ''
  },
  gap: {
    type: String,
    default: '0'
  },
  sortableFields: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  persistanceKey: {
    type: String,
    default: ''
  },
  defaultSortField: {
    type: String,
    default: ''
  },
  defaultSortAscending: Boolean,
  sortValueCallback: {
    type: Function,
    default: null
  },
  displayValueCallback: {
    type: Function,
    default: (fieldName: string) => `[${fieldName}]`
  },
  pageSize: {
    type: Number,
    default: Number.MAX_SAFE_INTEGER
  },
  paginationGap: {
    type: String,
    default: '48px'
  },
  mobileSeparators: {
    type: String as PropType<'between'|'surrounding'|'none'>,
    default: 'between'
  },
  mobileRowPadding: {
    type: String,
    default: '20px 0'
  },
  mobileGap: {
    type: String,
    default: '12px 16px'
  },
  overwriteMobile: Boolean,
  searchableCallback: {
    type: Function as PropType<(searchTerm: string, obj:unknown) => boolean>,
    default: null
  },
  filter: {
    type: Object as PropType<BcxTableFilter>,
    default: null
  },
  pinnedItems: {
    type: Array as PropType<BcxTablePinnedItemPosition[]>,
    default: () => []
  },
  isPaginationInHeader: Boolean,
},
  emits: ['visible-rows', 'search-filter-sort-changed'],
  setup(__props, { emit }) {

const props = __props;




const isSortable = computed(() => !!props.sortableFields.length);

const searchTerm = props.persistanceKey && props.searchableCallback
  ? useLocalStorage(`${props.persistanceKey}-tablesearch`, '')
  : ref('');
const filterValue = props.persistanceKey && props.filter
  ? useLocalStorage(`${props.persistanceKey}-tablefilter`, [])
  : ref([]);
const fieldName = props.persistanceKey && isSortable.value
  ? useLocalStorage(`${props.persistanceKey}-tablesort`, props.defaultSortField)
  : ref(props.defaultSortField);
const isAscending = props.persistanceKey && isSortable.value
  ? useLocalStorage(`${props.persistanceKey}-tabledir`, props.defaultSortAscending)
  : ref(props.defaultSortAscending);

const { rows, filter } = toRefs(props);

const page = ref(1);

const { isMobileWidth } = useResponsiveness();

const cssGridTemplateColumns = computed(() => (isMobileWidth.value ? `repeat(${props.headerFields.length}, auto)` : props.templateColumns));

const isMobile = computed(() => props.overwriteMobile || isMobileWidth.value);

const cssGridMobileAreas = computed(() => props.mobileGridAreas.split(/\n/).map((row) => `"${row}"`).join('\n'));

const filterItems = computed(() => {
  if (!filter.value) return [];

  return filter.value.states.map((state) => ({
    label: filter.value.labelCallback(state),
    value: state
  }));
});

watchImmediate(filter, (filter) => {
  if (!filter) return;
  if (filterValue.value.length) return;
  if (filter.defaultValue) filterValue.value = [filter.defaultValue];
  else if (filter.isExclusive) filterValue.value = [filter.states[0]];
  else filterValue.value = [...filter.states];
});

const hasContent = computed(() => !!rows.value.length);

const searchFilterSortOptions: BcxTableSearchFilterSortOptions = reactiveComputed(() => ({
  searchCallback: props.searchableCallback,
  sortValueCallback: props.sortValueCallback,
  filter: props.filter,

  states: filterValue.value,
  sortField: fieldName.value,
  isAscending: isAscending.value,
  searchTerm: searchTerm.value,

  pinnedItems: props.pinnedItems,
}));

const isFilterVisible = computed(() => (filter.value?.visibleStates ? filter.value.visibleStates.length : filter.value.states.length));

watch([filterValue, fieldName, isAscending, searchTerm], () => {
  page.value = 1;
  emit('search-filter-sort-changed');
});

const { finalRows } = useBcxTableSearchFilterSort(searchFilterSortOptions, rows);

const displayedHeaderFields = computed(() => {
  if (isMobile.value) {
    return props.sortableFields;
  }
  return props.headerFields;
});

watchImmediate(finalRows, (array) => {
  emit('visible-rows', array);
});


return { __sfc: true,props, emit, isSortable, searchTerm, filterValue, fieldName, isAscending, rows, filter, page, isMobileWidth, cssGridTemplateColumns, isMobile, cssGridMobileAreas, filterItems, hasContent, searchFilterSortOptions, isFilterVisible, finalRows, displayedHeaderFields, BCXSortableTableHeader, BCXPaginatedContent, SearchInput, BCXMultiselectFilter, BCXFilterButtons, BCXPagination }
}

})